<template>
  <span
    ref="triggerRef"
    class="ui-tooltip-trigger"
    :class="{ 'full-width': props.fullWidth }"
    @mouseenter="handleEnter"
    @mouseleave="handleLeave"
    @click="handleClick"
  >
    <slot />
  </span>
  <Teleport to="body">
    <div
      v-if="isOpen"
      ref="tooltipRef"
      class="ui-tooltip"
      role="tooltip"
      :style="tooltipStyle"
      @click.stop
      @mouseenter="handleTooltipEnter"
      @mouseleave="handleTooltipLeave"
    >
      <slot name="content" />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, onMounted, ref, watch } from "vue";
import { useTooltipRegistry } from "../../composables/useTooltip";

const props = withDefaults(
  defineProps<{
    placement?: "top" | "bottom";
    hoverDelay?: number;
    fullWidth?: boolean;
  }>(),
  {
    placement: "top",
    hoverDelay: 500,
    fullWidth: false
  }
);

const triggerRef = ref<HTMLElement | null>(null);
const tooltipRef = ref<HTMLElement | null>(null);
const tooltipStyle = ref<Record<string, string>>({});
const hoverTimer = ref<number | null>(null);
const isHoveringTooltip = ref(false);
const id = crypto.randomUUID();
const { activeTooltipId, openTooltip, closeTooltip, registerTooltip } = useTooltipRegistry();
let unregister: (() => void) | null = null;

const isOpen = computed(() => activeTooltipId.value === id);

function clearHoverTimer() {
  if (hoverTimer.value) {
    window.clearTimeout(hoverTimer.value);
    hoverTimer.value = null;
  }
}

function handleEnter() {
  clearHoverTimer();
  hoverTimer.value = window.setTimeout(() => openTooltip(id), props.hoverDelay);
}

function handleLeave() {
  clearHoverTimer();
  window.setTimeout(() => {
    if (!isHoveringTooltip.value) closeTooltip(id);
  }, 60);
}

function handleTooltipEnter() {
  isHoveringTooltip.value = true;
}

function handleTooltipLeave() {
  isHoveringTooltip.value = false;
  closeTooltip(id);
}

function handleClick(event: MouseEvent) {
  event.stopPropagation();
  if (isOpen.value) {
    closeTooltip(id);
  } else {
    openTooltip(id);
  }
}

function positionTooltip() {
  const triggerEl = triggerRef.value;
  const tooltipEl = tooltipRef.value;
  if (!triggerEl || !tooltipEl) return;

  const triggerRect = triggerEl.getBoundingClientRect();
  const tooltipRect = tooltipEl.getBoundingClientRect();
  const padding = 12;

  let top = props.placement === "top"
    ? triggerRect.top - tooltipRect.height - 10
    : triggerRect.bottom + 10;

  let left = triggerRect.left + triggerRect.width / 2 - tooltipRect.width / 2;

  const minLeft = padding;
  const maxLeft = window.innerWidth - tooltipRect.width - padding;
  left = Math.min(Math.max(left, minLeft), maxLeft);

  const minTop = padding;
  const maxTop = window.innerHeight - tooltipRect.height - padding;
  top = Math.min(Math.max(top, minTop), maxTop);

  tooltipStyle.value = {
    top: `${top}px`,
    left: `${left}px`
  };
}

function handleDocumentClick() {
  closeTooltip(id);
}

watch(isOpen, async (open) => {
  if (!open) return;
  await nextTick();
  positionTooltip();
});

onMounted(() => {
  unregister = registerTooltip(id, { position: positionTooltip, close: handleDocumentClick });
});

onBeforeUnmount(() => {
  clearHoverTimer();
  unregister?.();
  unregister = null;
});
</script>

<style scoped>
.ui-tooltip-trigger {
  display: inline-flex;
  align-items: center;
}

.ui-tooltip-trigger.full-width {
  display: flex;
  width: 100%;
  min-width: 0;
}

.ui-tooltip {
  position: fixed;
  z-index: 3960;
  background: color-mix(in srgb, var(--bg-2) 92%, transparent);
  border: 1px solid var(--border-1);
  padding: 0.65rem 0.9rem;
  border-radius: var(--radius-1);
  box-shadow: var(--shadow-1);
  max-width: 260px;
  font-size: 0.85rem;
  color: var(--fg-0);
}
</style>
